import React, { useState } from 'react';
import { api } from '../lib/api';
import { money } from '../lib/format';
import type { Building, Property, PropertyStatus, PropertyType, Room } from '../lib/types';
import { PinPicker, PlaceFields, findCity, type Pin, type PlaceValue } from '../components/Places';
import { Confirm, Drawer, Failed, Loading, Stat, TaskRow, type Ctx } from '../main';
import { useResource } from '../lib/hooks';

export const STATUS_LABEL: Record<PropertyStatus, string> = {
  UnderTender: 'Under tender', Maintenance: 'Maintenance', Operating: 'Operating', Released: 'Released',
};
const STATUS_TONE: Record<PropertyStatus, string> = {
  UnderTender: 't-warn', Maintenance: 't-bad', Operating: 't-ok', Released: 't-mute',
};
export const StatusTag = ({ status }: { status: PropertyStatus }): React.JSX.Element =>
  <span className={`tag ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>;

export function Properties({ ctx }: { ctx: Ctx }): React.JSX.Element {
  const { data, loading, error, reload } = useResource(() => api.properties(), []);
  const [open, setOpen] = useState<Property | null>(null);
  const [adding, setAdding] = useState(false);

  if (loading) return <Loading />;
  if (error) return <Failed error={error} onRetry={reload} />;
  const properties = data?.properties ?? [];
  const rooms = properties.reduce((a, b) => a + b.roomCount, 0);
  const occupied = properties.reduce((a, b) => a + b.occupiedCount, 0);
  const rentRoll = properties.reduce((a, b) => a + Number(b.rentRoll), 0);

  const changed = async (): Promise<void> => { await Promise.all([reload(), ctx.refresh()]); };

  return (
    <>
      <div className="grid g3" style={{ marginBottom: 16 }}>
        <Stat label="Sites" value={properties.length} />
        <Stat label="Rooms let" value={`${occupied}/${rooms}`} tone="good"
          foot={rooms ? `${Math.round((occupied / rooms) * 100)}% occupancy` : undefined} />
        {ctx.canManage
          ? <Stat label="Rent roll" value={money(rentRoll)} foot="Per month, contracted" />
          : <Stat label="Vacant" value={rooms - occupied} />}
      </div>

      <div className="bar">
        <button className="btn sm" onClick={() => ctx.go('map')}>Show on the map</button>
        {ctx.canManage && (
          <button className="btn sm accent w" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>
            Add a property
          </button>
        )}
      </div>

      <div className="card"><table className="responsive">
        <thead><tr><th>Property</th><th>Area</th><th>Rooms</th>{ctx.canManage && <th>Rent roll</th>}<th>Status</th></tr></thead>
        <tbody>{properties.map((p) => (
          <tr key={p.id} className="clk" onClick={() => setOpen(p)}>
            <td data-label="Property"><b>{p.name}</b>
              <div className="sub">{[typeLine(p, ctx.buildings), p.address].filter(Boolean).join(' · ')}</div></td>
            <td data-label="Area">{[p.district, p.city].filter(Boolean).join(', ') || '—'}</td>
            <td data-label="Rooms">
              <span className="mono">{p.occupiedCount}/{p.roomCount}</span>
              <span className="occ" style={{ maxWidth: 90, marginLeft: 8 }}>
                {Array.from({ length: Math.min(p.roomCount, 10) }, (_, i) => (
                  <i key={i} className={i < p.occupiedCount ? 'on' : ''} />
                ))}
              </span>
            </td>
            {ctx.canManage && <td data-label="Rent roll" className="mono">{money(p.rentRoll)}</td>}
            <td data-label="Status"><StatusTag status={p.status} /></td>
          </tr>
        ))}</tbody>
      </table>
      {properties.length === 0 && (
        <div className="empty">
          <b>No properties yet</b>
          Add one under tender to start the Tender workflow, or one you already hold.
        </div>
      )}
      </div>

      {open && (
        <PropertyDrawer ctx={ctx} property={open} onClose={() => setOpen(null)}
          onChanged={async () => { await changed(); setOpen(null); }} />
      )}
      {adding && (
        <AddProperty ctx={ctx} onClose={() => setAdding(false)}
          onAdded={async (title, detail) => { await changed(); setAdding(false); ctx.toast(title, detail); }} />
      )}
    </>
  );
}

/* ------------------------------------------------------------- one site */

export function PropertyDrawer({ ctx, property, onClose, onChanged }: {
  ctx: Ctx; property: Property; onClose: () => void; onChanged: () => Promise<void>;
}): React.JSX.Element {
  const rooms = useResource(() => api.rooms(property.id), [property.id]);
  const tasks = useResource(() => api.tasks({ status: 'open', propertyId: property.id, limit: 50 }), [property.id]);
  const [mode, setMode] = useState<'view' | 'edit' | 'room'>('view');
  const [room, setRoom] = useState<Room | null>(null);
  const [removing, setRemoving] = useState<Room | null>(null);

  if (removing) {
    return (
      <Confirm title={`Delete room ${removing.number}?`} danger action="Delete room"
        detail="An occupied room cannot be deleted, and nor can one with lease history — that history is the record."
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          await api.deleteRoom(removing.id);
          setRemoving(null); await rooms.reload(); await ctx.refresh();
          ctx.toast('Room deleted', removing.number);
        }} />
    );
  }
  if (mode === 'edit') {
    return <EditProperty ctx={ctx} property={property} onClose={() => setMode('view')} onSaved={onChanged} />;
  }
  if (mode === 'room') {
    return (
      <RoomForm ctx={ctx} property={property} room={room} onClose={() => { setMode('view'); setRoom(null); }}
        onSaved={async () => { setMode('view'); setRoom(null); await rooms.reload(); await ctx.refresh(); }} />
    );
  }

  const open = tasks.data?.tasks ?? [];
  return (
    <Drawer title={property.name}
      subtitle={[property.address, property.district, property.city].filter(Boolean).join(', ')}
      onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Close</button>
        <button className="btn w" onClick={() => ctx.newTask({ propertyId: property.id })}>New task here</button>
        {ctx.canManage && <button className="btn accent w" onClick={() => setMode('edit')}>Edit property</button>}
      </>}>
      <div className="bar"><StatusTag status={property.status} />
        <span className="tag t-mute">{typeLine(property, ctx.buildings)}</span>
        {property.latitude === null && <span className="tag t-mute">no map position</span>}
      </div>
      <div className="grid g3" style={{ marginBottom: 16 }}>
        <Stat label="Rooms" value={`${property.occupiedCount}/${property.roomCount}`} />
        <Stat label="Vacant" value={property.roomCount - property.occupiedCount}
          tone={property.roomCount - property.occupiedCount ? 'hot' : 'good'} />
        {ctx.canManage && <Stat label="Rent roll" value={money(property.rentRoll)} />}
      </div>

      <div className="between">
        <h3 style={{ fontSize: 14 }}>Rooms</h3>
        {ctx.canManage && (
          <button className="btn sm w" onClick={() => { setRoom(null); setMode('room'); }}>Add a room</button>
        )}
      </div>
      <div style={{ marginTop: 12 }}>
        {rooms.loading && <Loading />}
        {rooms.data?.rooms.map((r) => (
          <div className="rowcard" key={r.id}>
            <span className="body">
              <span className="tt">Room {r.number}</span>
              <span className="meta">
                <span className={`tag t-${r.status === 'Occupied' ? 'ok' : 'warn'}`}>{r.status}</span>
                {/* a specialist's copy of the rooms carries no rents: the API strips them */}
                {ctx.canManage && (r.currentRent > 0
                  ? <span className="mono sub">{money(r.currentRent)}/mo · set by the lease</span>
                  : <span className="sub">asking {money(r.askingLow)}–{money(r.askingHigh)}</span>)}
              </span>
            </span>
            {ctx.canManage && (
              <span className="w" style={{ display: 'flex', gap: 6 }}>
                <button className="btn sm" onClick={() => { setRoom(r); setMode('room'); }}>Edit</button>
                <button className="btn sm quiet" style={{ color: 'var(--coral)' }} disabled={r.status === 'Occupied'}
                  title={r.status === 'Occupied' ? 'Occupied — move the tenant out first' : undefined}
                  onClick={() => setRemoving(r)}>Delete</button>
              </span>
            )}
          </div>
        ))}
        {rooms.data?.rooms.length === 0 && (
          <div className="note">No rooms yet. The Tender workflow creates them when the contract is signed.</div>
        )}
      </div>

      <h3 className="sec" style={{ fontSize: 14 }}>Open work here</h3>
      {tasks.loading && <Loading />}
      {open.map((t) => <TaskRow key={t.id} task={t} ctx={ctx} />)}
      {tasks.data && open.length === 0 && <div className="note ok">Nothing open on this property.</div>}
    </Drawer>
  );
}

/* ------------------------------------------------------------ where it is */

interface Where { place: PlaceValue; address: string; pin: Pin | null }

const TYPE_LABEL: Record<PropertyType, string> = { Villa: 'Villa', Building: 'Whole building', Floor: 'Floor' };

/** What a property is, in words: "Floor 12 · Harbour Tower", "Villa", "Whole building". */
export function typeLine(p: Property, buildings: Building[]): string {
  const b = buildings.find((x) => x.id === p.buildingId);
  if (p.kind === 'Floor') return `Floor ${p.floor ?? '?'}${b ? ` · ${b.name}` : ''}`;
  if (p.kind === 'Building') return b && b.name !== p.name ? `Whole building · ${b.name}` : 'Whole building';
  return 'Villa';
}

/** The place fields and the map, for a villa or a building. */
function WhereFields({ ctx, value, onChange, pinRequired, idPrefix }: {
  ctx: Ctx; value: Where; onChange: (w: Where) => void; pinRequired: boolean; idPrefix: string;
}): React.JSX.Element {
  return (
    <>
      <PlaceFields locations={ctx.locations} value={value.place} idPrefix={idPrefix}
        onChange={(place) => onChange({ ...value, place })} />
      <div className="field">
        <label htmlFor={`${idPrefix}-address`}>Street address</label>
        <input id={`${idPrefix}-address`} value={value.address} placeholder="Sheikh Zayed Road"
          onChange={(e) => onChange({ ...value, address: e.target.value })} />
      </div>
      <PinPicker value={value.pin} onChange={(pin) => onChange({ ...value, pin })} required={pinRequired}
        locations={ctx.locations} place={value.place} />
    </>
  );
}

const whereOf = (ctx: Ctx, x: { cityId: string | null; districtId: string | null; city: string; address: string;
  latitude: number | null; longitude: number | null }): Where => ({
  // filed before the lists existed: the city is matched by name
  place: { cityId: x.cityId ?? findCity(ctx.locations, null, x.city)?.id ?? null, districtId: x.districtId },
  address: x.address,
  pin: x.latitude !== null && x.longitude !== null ? { lat: x.latitude, lng: x.longitude } : null,
});

const whereBody = (w: Where): Record<string, unknown> => ({
  cityId: w.place.cityId, districtId: w.place.districtId, address: w.address,
  latitude: w.pin?.lat ?? null, longitude: w.pin?.lng ?? null,
});

function EditProperty({ ctx, property, onClose, onSaved }: {
  ctx: Ctx; property: Property; onClose: () => void; onSaved: () => Promise<void>;
}): React.JSX.Element {
  const building = ctx.buildings.find((b) => b.id === property.buildingId) ?? null;
  const [form, setForm] = useState({ name: property.name, status: property.status, floor: property.floor ?? '' });
  const [where, setWhere] = useState<Where>(() => whereOf(ctx, property));
  const [editingBuilding, setEditingBuilding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const villa = property.kind === 'Villa';

  if (editingBuilding && building) {
    return <BuildingForm ctx={ctx} building={building} onClose={() => setEditingBuilding(false)} onSaved={onSaved} />;
  }

  const save = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      await api.updateProperty(property.id, {
        name: form.name, status: form.status,
        ...(property.kind === 'Floor' ? { floor: form.floor } : {}),
        // a building or floor stands where its building does; that is edited on the building
        ...(villa ? whereBody(where) : {}),
      });
      ctx.toast('Property saved', form.name);
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title="Edit property" subtitle={`${property.name} · ${typeLine(property, ctx.buildings)}`} onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!form.name || (property.kind === 'Floor' && !form.floor) || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      <div className="field">
        <label htmlFor="p-name">Name</label>
        <input id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div className="frow">
        <div className="field">
          <label htmlFor="p-status">Status</label>
          <select id="p-status" value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as PropertyStatus })}>
            {(Object.keys(STATUS_LABEL) as PropertyStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
        {property.kind === 'Floor' && (
          <div className="field">
            <label htmlFor="p-floor">Floor</label>
            <input id="p-floor" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} />
          </div>
        )}
      </div>
      {villa ? (
        <WhereFields ctx={ctx} value={where} onChange={setWhere} pinRequired={false} idPrefix="p" />
      ) : (
        <div className="note" style={{ marginTop: 6 }}>
          {building ? (
            <>
              Its location is <b>{building.name}</b>&apos;s:{' '}
              {[building.address, building.district, building.city].filter(Boolean).join(', ') || 'no address yet'}.
              {(building.propertyCount ?? 1) > 1 && ` Shared with ${(building.propertyCount ?? 1) - 1} other propert${(building.propertyCount ?? 1) === 2 ? 'y' : 'ies'}.`}
              <div style={{ marginTop: 8 }}>
                <button className="btn sm" onClick={() => setEditingBuilding(true)}>Edit the building</button>
              </div>
            </>
          ) : 'Its location is its building’s.'}
        </div>
      )}
    </Drawer>
  );
}

/** A building's name and place. Moving it moves every property in it. */
function BuildingForm({ ctx, building, onClose, onSaved }: {
  ctx: Ctx; building: Building; onClose: () => void; onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [name, setName] = useState(building.name);
  const [where, setWhere] = useState<Where>(() => whereOf(ctx, building));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const others = (building.propertyCount ?? 1) - 1;

  const save = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      await api.updateBuilding(building.id, { name, ...whereBody(where) });
      ctx.toast('Building saved', others > 0 ? `${name} and the ${others + 1} properties in it` : name);
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title="Edit building" subtitle={building.name} onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!name || !where.place.cityId || !where.pin || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save building'}
        </button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      {others > 0 && (
        <div className="note warn" style={{ marginBottom: 12 }}>
          {others + 1} properties are in this building. Moving it moves all of them.
        </div>
      )}
      <div className="field">
        <label htmlFor="b-name">Building or tower name</label>
        <input id="b-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <WhereFields ctx={ctx} value={where} onChange={setWhere} pinRequired idPrefix="b" />
    </Drawer>
  );
}

function RoomForm({ ctx, property, room, onClose, onSaved }: {
  ctx: Ctx; property: Property; room: Room | null; onClose: () => void; onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [number, setNumber] = useState(room?.number ?? '');
  const [low, setLow] = useState(String(room?.askingLow ?? 2500));
  const [high, setHigh] = useState(String(room?.askingHigh ?? 3200));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      const b = { number, askingLow: Number(low), askingHigh: Number(high) };
      if (room) await api.updateRoom(room.id, b); else await api.createRoom(property.id, b);
      ctx.toast(room ? 'Room saved' : 'Room added', room ? number : 'It is vacant until a lease is created against it.');
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title={room ? `Room ${room.number}` : 'Add a room'} subtitle={property.name} onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!number || Number(high) < Number(low) || busy} onClick={save}>
          {busy ? 'Saving…' : room ? 'Save changes' : 'Add room'}
        </button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      <div className="field">
        <label htmlFor="rn">Room number</label>
        <input id="rn" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="05" />
      </div>
      <div className="frow">
        <div className="field">
          <label htmlFor="lo">Asking from</label>
          <input id="lo" type="number" value={low} onChange={(e) => setLow(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="hi">Asking to</label>
          <input id="hi" type="number" value={high} onChange={(e) => setHigh(e.target.value)} />
        </div>
      </div>
      {room && (
        <>
          <div className="frow">
            <div className="field"><label>Status</label><input value={room.status} disabled /></div>
            <div className="field"><label>Current rent</label><input value={room.currentRent ? money(room.currentRent) : '—'} disabled /></div>
          </div>
          <div className="hint">Status, tenant and current rent are set by the lease, not here.</div>
        </>
      )}
    </Drawer>
  );
}

/* --------------------------------------------------------------- adding */

/**
 * A property arrives one of two ways, and they are different things.
 *
 * Under tender: the Tender workflow is about to run against it. It has no rooms
 * yet — signing the contract creates them, with every instalment. Already held:
 * it is operating now, so its rooms are created here.
 *
 * And it is one of three things: a villa, with a location of its own; a whole
 * building; or one floor of a building. A building is entered once, with its
 * name and pin, and every floor taken in it afterwards is filed under it.
 */
function AddProperty({ ctx, onClose, onAdded }: {
  ctx: Ctx; onClose: () => void; onAdded: (title: string, detail?: string) => Promise<void>;
}): React.JSX.Element {
  const tender = ctx.catalogue.rules.find((r) => r.manualEntry && r.workflow === 'Tender') ?? null;
  const [arrival, setArrival] = useState<'tender' | 'held'>(tender ? 'tender' : 'held');
  const [startTender, setStartTender] = useState(true);
  const [type, setType] = useState<PropertyType>('Floor');
  const [buildingId, setBuildingId] = useState<string>(ctx.buildings[0]?.id ?? 'new');
  const [buildingName, setBuildingName] = useState('');
  const [where, setWhere] = useState<Where>(() => ({
    // one city of operation: chosen already
    place: { cityId: ctx.locations?.countries.flatMap((k) => k.cities).length === 1
      ? ctx.locations.countries[0]!.cities[0]!.id : null, districtId: null },
    address: '', pin: null,
  }));
  const [name, setName] = useState('');
  const [floor, setFloor] = useState('');
  const [rooms, setRooms] = useState('4');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isNew = type !== 'Villa' && buildingId === 'new';
  const existing = ctx.buildings.find((b) => b.id === buildingId) ?? null;
  const suggested = type === 'Floor' && existing && floor ? `${existing.name} · Floor ${floor}`
    : type === 'Floor' && isNew && buildingName && floor ? `${buildingName} · Floor ${floor}`
    : type === 'Building' ? (existing?.name ?? buildingName) : '';

  const ready = (type === 'Villa' ? Boolean(name.trim() && where.place.cityId) : true)
    && (!isNew || Boolean(buildingName.trim() && where.place.cityId && where.pin))
    && (type !== 'Floor' || Boolean(floor.trim()));

  const save = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      const b: Record<string, unknown> = {
        kind: type,
        status: arrival === 'tender' ? 'UnderTender' : 'Operating',
        rooms: arrival === 'tender' ? 0 : Number(rooms),
        ...(name.trim() ? { name: name.trim() } : {}),
      };
      if (type === 'Villa') Object.assign(b, whereBody(where));
      else if (isNew) b.building = { name: buildingName.trim(), ...whereBody(where) };
      else b.buildingId = buildingId;
      if (type === 'Floor') b.floor = floor.trim();

      const { property } = await api.createProperty(b);
      if (arrival === 'tender' && startTender && tender) {
        await api.createTask({ name: tender.name, workflow: tender.workflow, propertyId: property.id });
        await onAdded('Property added', `The ${tender.workflow} workflow has started: ${tender.name}.`);
      } else {
        await onAdded('Property added', property.name);
      }
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Drawer title="Add a property" onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!ready || busy} onClick={save}>
          {busy ? 'Saving…' : 'Add'}
        </button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      {tender && (
        <>
          <button className={`opt ${arrival === 'tender' ? 'on' : ''}`} onClick={() => setArrival('tender')}>
            <span className="k" />
            <span><b>A unit we are bidding for</b>
              <span>Under tender. The rooms and every instalment are created when the contract is signed.</span></span>
          </button>
          <button className={`opt ${arrival === 'held' ? 'on' : ''}`} onClick={() => setArrival('held')}>
            <span className="k" />
            <span><b>One we already hold</b><span>Operating now. Its rooms are created here.</span></span>
          </button>
        </>
      )}

      <div className="field" style={{ marginTop: 12 }}>
        <label>What it is</label>
        <div className="seg">
          {(['Floor', 'Building', 'Villa'] as const).map((t) => (
            <button key={t} type="button" className={type === t ? 'on' : ''} onClick={() => setType(t)}>
              {t === 'Floor' ? 'A floor in a building' : TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      </div>

      {type !== 'Villa' && (
        <div className="field">
          <label htmlFor="a-building">Building or tower</label>
          <select id="a-building" value={buildingId} onChange={(e) => setBuildingId(e.target.value)}>
            {ctx.buildings.map((b) => (
              <option key={b.id} value={b.id}>{b.name}{b.district ? ` — ${b.district}` : ''}</option>
            ))}
            <option value="new">＋ A building not listed yet</option>
          </select>
          {existing && (
            <div className="hint">
              {[existing.address, existing.district, existing.city].filter(Boolean).join(', ') || 'No address yet'}
              {existing.latitude === null ? ' · not on the map yet' : ''}
            </div>
          )}
        </div>
      )}

      {isNew && (
        <div className="field">
          <label htmlFor="a-bname">New building or tower name</label>
          <input id="a-bname" value={buildingName} placeholder="Marina Heights"
            onChange={(e) => setBuildingName(e.target.value)} />
        </div>
      )}

      {type === 'Floor' && (
        <div className="field">
          <label htmlFor="a-floor">Floor</label>
          <input id="a-floor" value={floor} placeholder="12" onChange={(e) => setFloor(e.target.value)} />
        </div>
      )}

      <div className="field">
        <label htmlFor="a-name">Name{type === 'Villa' ? '' : ' (optional)'}</label>
        <input id="a-name" value={name} placeholder={suggested || (type === 'Villa' ? 'Villa 14, Al Safa' : '')}
          onChange={(e) => setName(e.target.value)} />
        {type !== 'Villa' && suggested && !name && <div className="hint">Left empty, it is called “{suggested}”.</div>}
      </div>

      {(type === 'Villa' || isNew) && (
        <WhereFields ctx={ctx} value={where} onChange={setWhere} pinRequired={isNew} idPrefix="a" />
      )}

      {arrival === 'held' ? (
        <div className="field">
          <label htmlFor="a-rooms">Rooms to create</label>
          <input id="a-rooms" type="number" min={0} value={rooms} onChange={(e) => setRooms(e.target.value)} />
        </div>
      ) : (
        <label className="btn full" style={{ justifyContent: 'flex-start', gap: 10 }}>
          <input type="checkbox" checked={startTender} onChange={(e) => setStartTender(e.target.checked)} />
          Start the {tender?.workflow} workflow now
        </label>
      )}
    </Drawer>
  );
}
